import { loadHeroLanding } from '../../../blocks/hero-landing/scripts/hero-landing';
import { breakpoints } from '../config/breakpoints';

// Media query for when the WP admin bar switches from sticky to static
const adminBarStickyMQ = window.matchMedia('(width <= 600px)');

const touchMQ = window.matchMedia('(pointer: coarse)');
const largeMQ = window.matchMedia(`(width > ${breakpoints.lg}px)`);

let header;
let navPrimary;
let navSecondary;
let navMenu;
let skipLink;
let rAF;
let prevScroll = window.scrollY;
let prevTime = Date.now();
let accountWidgetInit = false;
let accountPointerListenerBound = false;
let accountTouchListenerBound = false;

/**
 * Calculates the value of --nav-height and --primary-nav-height.
 * --live-nav-height is left in for backwards compatibility.
 */
function calculateHeaderHeight() {
  const primary = navPrimary.closest('.header__wrapper');

  document.documentElement.style.setProperty('--nav-height', `${header.offsetHeight}px`);
  document.documentElement.style.setProperty('--live-nav-height', `${header.offsetHeight}px`);
  document.documentElement.style.setProperty('--primary-nav-height', `${primary.offsetHeight}px`);
}

/**
 * Gets the height of the admin bar for use in fixed positioning contexts.
 */
function getAdminBarHeight() {
  return parseInt(
    getComputedStyle(document.documentElement)
      .getPropertyValue('--wp-admin--admin-bar--height'),
    10,
  ) || 0;
}

/**
 * Updates the value of --nav-bottom.
 * @param {boolean} once - If true, only runs once. If false, creates a requestAnimationFrame loop
 */
function updateStickyHeight(once = false) {
  const { bottom } = header.getBoundingClientRect();
  const adminBarHeight = adminBarStickyMQ.matches ? 0 : getAdminBarHeight();

  if ('fixed' in header.dataset) {
    document.documentElement.style.setProperty(
      '--nav-bottom',
      `${Math.max(bottom, adminBarHeight)}px`,
    );
  } else {
    document.documentElement.style.setProperty('--nav-bottom', `${adminBarHeight}px`);
  }

  if (!once) {
    rAF = requestAnimationFrame(updateStickyHeight.bind(this, false));
  }

  if (header.classList.contains('header--transparent')) {
    document.documentElement.style.setProperty('--nav-underflow', `${adminBarHeight}px`);
  }
}

/**
 * Attaches event listeners that track and update the sticky height variable
 */
function setShowHideListener() {
  updateStickyHeight(true);

  header.addEventListener('transitionrun', () => {
    cancelAnimationFrame(rAF);
    updateStickyHeight();
  });

  header.addEventListener('transitionend', () => {
    cancelAnimationFrame(rAF);
    updateStickyHeight(true);
  });
}

/**
 * Creates an event listener for each breakpoint registered in
 * config/breakpoints.
 */
function setBreakpointListeners() {
  Object.values(breakpoints)
    .map((value) => Number.parseInt(value, 10))
    .toSorted((a, b) => a > b)
    .forEach((breakpoint, index, list) => {
      let query;
      if (index > 0) {
        query = `(${list[index - 1]}px < width <= ${breakpoint}px)`;
        window.matchMedia(query).addEventListener('change', (e) => {
          if (e.matches) {
            calculateHeaderHeight();
          }
        });
      }
    });

  adminBarStickyMQ.addEventListener('change', () => {
    updateStickyHeight(true);
  });
}

/**
 * Sets the scroll listener that is used to show and hide the nav bar.
 */
function setScrollListener() {
  document.addEventListener('scroll', () => {
    const now = Date.now();
    const scroll = window.scrollY;
    const deltaDistance = window.scrollY - prevScroll;
    const deltaTime = now - prevTime;
    const velocity = deltaDistance / deltaTime;

    const isStuck = 'fixed' in header.dataset;
    const top = adminBarStickyMQ.matches ? getAdminBarHeight() : 0;

    if (header.dataset.fixed === 'submenu') {
      return;
    }

    if (!isStuck && window.scrollY > header.offsetHeight + top) {
      header.dataset.fixed = '';
    } else if (isStuck && window.scrollY <= top) {
      delete header.dataset.fixed;
      updateStickyHeight(true);
    } else if (isStuck) {
      if (window.scrollY <= header.offsetHeight + top && header.dataset.fixed === 'hide') {
        delete header.dataset.fixed;
        updateStickyHeight(true);
      } else if (velocity > 0.25) {
        header.dataset.fixed = 'hide';
      } else if (velocity < -0.35) {
        header.dataset.fixed = 'show';
      }
    }

    prevTime = now;
    prevScroll = scroll;
  });
}

function initAccountWidget() {
  const openClass = 'nav__account--open';
  const widget = header.querySelector('.nav__account');

  function toggleAccountOpen(event) {
    if (!widget) {
      return;
    }

    const isOpen = widget.classList.contains(openClass);
    const pointerType = ('pointerType' in event) ? event.pointerType : '';

    if (!event.target.closest('.nav__account')) {
      if (isOpen) {
        widget.classList.remove(openClass);
      }
      return;
    }

    if (event.target.closest('a')) {
      return;
    }

    if (pointerType && pointerType !== 'touch') {
      return;
    }

    if (isOpen) {
      widget.classList.remove(openClass);
      setTimeout(() => {
        widget.classList.remove('clickable');
      }, 1000);
    } else {
      widget.classList.add(openClass);
      setTimeout(() => {
        widget.classList.add('clickable');
      }, 1000);
    }
  }

  if (!accountPointerListenerBound) {
    if (window.PointerEvent) {
      document.addEventListener('pointerup', toggleAccountOpen);
      accountPointerListenerBound = true;
    }
  }

  if (!accountTouchListenerBound) {
    if (!window.PointerEvent) {
      document.addEventListener('touchend', toggleAccountOpen);
      accountTouchListenerBound = true;
    }
  }

  if (!accountWidgetInit) {
    touchMQ.addEventListener('change', initAccountWidget);
    accountWidgetInit = true;
  }
}

/**
 * Initializes the hamburger button and overlay nav
 */
function initNavMenu() {
  if (!navMenu) {
    return;
  }

  const main = document.querySelector('main#main');
  const parent = navMenu.querySelector('.nav-menu__menu[data-menu="menu-parent"]');
  const openButton = document.querySelector('.nav__hamburger-button');
  const closeButton = navMenu.querySelector('.nav-menu__close');
  const parentMedia = navMenu.querySelector('.nav-menu__media-item[data-menu="menu-parent"]');
  const topLevelButtons = navMenu.querySelectorAll('button[data-menu]');
  const backButtons = navMenu.querySelectorAll('.nav-menu__back');

  function toggleItemVisible(element, visible) {
    element.setAttribute('aria-hidden', visible ? 'false' : 'true');
    // eslint-disable-next-line no-param-reassign
    element.dataset.visible = visible ? 'true' : 'false';
    if (element === navMenu) {
      element.setAttribute('aria-expanded', visible ? 'true' : 'false');
    }

    /* eslint-disable-next-line no-param-reassign */
    element.inert = !visible;
  }

  function togglePrimaryInert(event) {
    navPrimary.inert = event.matches;
  }

  function onAfterClose(event) {
    if (event.propertyName !== 'height') {
      return;
    }

    navMenu.style.opacity = 0;
    main.style.zIndex = '';

    document.body.classList.remove('lock-scroll');

    const currentSubMenu = document.querySelector('.nav-menu__menu--sub[data-visible="true"]');
    if (currentSubMenu) {
      const menuID = currentSubMenu.dataset.menu;
      const currentMedia = navMenu.querySelector(`.nav-menu__media-item[data-menu="${menuID}"]`);
      const parentButton = parent.querySelector(`.nav-menu__menu-item[data-menu="${menuID}"]`);

      toggleItemVisible(parent, true);
      parent.classList.add('nav-menu__menu--active');
      toggleItemVisible(currentSubMenu, false);
      parentButton.setAttribute('aria-expanded', 'false');
      currentSubMenu.classList.remove('nav-menu__menu--active');
      parentMedia.setAttribute('aria-hidden', 'false');
      parentMedia.classList.add('nav-menu__media-item--active');
      parentMedia.inert = false;
      currentMedia.setAttribute('aria-hidden', 'true');
      currentMedia.classList.remove('nav-menu__media-item--active');
      currentMedia.inert = true;

      if (parentMedia.querySelector('video')) {
        parentMedia.querySelector('video').mediaControl.pause();
      }

      if (currentMedia.querySelector('video')) {
        currentMedia.querySelector('video').mediaControl.pause();
      }

      /* eslint-disable-next-line no-use-before-define */
      document.removeEventListener('keydown', navigateUpOnEscape);
    }
  }

  /**
   * Opens the overlay menu
   */
  function openMenu() {
    navMenu.removeEventListener('transitionend', onAfterClose, { once: true });
    largeMQ.addEventListener('change', togglePrimaryInert);

    header.dataset.fixed = 'submenu';
    document.body.classList.add('lock-scroll');

    navMenu.style.opacity = 1;
    toggleItemVisible(navMenu, true);

    main.inert = true;
    main.style.zIndex = 0;
    navPrimary.inert = largeMQ.matches;
    skipLink.tabIndex = -1;

    if (navSecondary) {
      navSecondary.inert = true;
      navSecondary.classList.add('nav-secondary--hidden');
    }

    openButton.ariaPressed = true;
    openButton.setAttribute('aria-expanded', 'true');
    closeButton.focus();
  }

  /**
   * Hides the overlay menu
   */
  function closeMenu() {
    navMenu.addEventListener('transitionend', onAfterClose, { once: true });
    largeMQ.removeEventListener('change', togglePrimaryInert);

    const top = adminBarStickyMQ.matches ? getAdminBarHeight() : 0;
    if (window.scrollY <= header.offsetHeight + top) {
      delete header.dataset.fixed;
      updateStickyHeight(true);
    } else {
      header.dataset.fixed = 'show';
    }

    toggleItemVisible(navMenu, false);

    main.inert = false;
    navPrimary.inert = false;
    skipLink.tabIndex = 0;

    if (navSecondary) {
      navSecondary.inert = false;
      navSecondary.classList.remove('nav-secondary--hidden');
    }

    if (header.dataset.fixed === 'hide') {
      header.dataset.fixed = 'show';
    }

    openButton.ariaPressed = false;
    openButton.setAttribute('aria-expanded', 'false');
    openButton.focus();
  }

  /**
   * Escape key keydown event handler for closing the overlay nav
   * @param {KeyboardEvent} event
   */
  function closeOnEscape(event) {
    if (event.key === 'Escape') {
      closeMenu();
    }
  }

  /**
   * Escape key keydown event handler for closing a subnav menu
   * @param {KeyboardEvent} event
   */
  function navigateUpOnEscape(event) {
    if (event.key === 'Escape') {
      /* eslint-disable-next-line no-use-before-define */
      navigateUp();
    }
  }

  /**
   * Navigate up one level from subnav to main overlay nav
   */
  function navigateUp() {
    const currentMenu = document.querySelector('.nav-menu__menu[data-visible="true"]');
    const menuID = currentMenu.dataset.menu;
    const currentMedia = navMenu.querySelector(`.nav-menu__media-item[data-menu="${menuID}"]`);
    const parentButton = parent.querySelector(`.nav-menu__menu-item[data-menu="${menuID}"]`);

    toggleItemVisible(parent, true);
    parent.classList.add('nav-menu__menu--active');
    toggleItemVisible(currentMenu, false);
    currentMenu.classList.remove('nav-menu__menu--active');
    parentMedia.setAttribute('aria-hidden', 'false');
    parentMedia.classList.add('nav-menu__media-item--active');
    parentMedia.inert = false;
    currentMedia.setAttribute('aria-hidden', 'true');
    currentMedia.classList.remove('nav-menu__media-item--active');
    currentMedia.inert = true;

    if (parentMedia.querySelector('video')) {
      parentMedia.querySelector('video').mediaControl.playIfAble();
    }

    if (currentMedia.querySelector('video')) {
      currentMedia.querySelector('video').mediaControl.pause();
    }

    parentButton.focus();
    parentButton.setAttribute('aria-expanded', 'false');

    document.removeEventListener('keydown', navigateUpOnEscape);
    document.addEventListener('keydown', closeOnEscape);
  }

  closeButton.addEventListener('click', closeMenu);
  openButton.addEventListener('click', () => {
    if (openButton.ariaPressed === 'true') {
      closeMenu();
    } else {
      openMenu();
    }
  });

  topLevelButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const menuID = button.dataset.menu;
      const targetMenu = navMenu.querySelector(`.nav-menu__menu[data-menu="${menuID}"]`);
      const targetMedia = navMenu.querySelector(`.nav-menu__media-item[data-menu="${menuID}"]`);

      toggleItemVisible(parent, false);
      parent.classList.remove('nav-menu__menu--active');
      toggleItemVisible(targetMenu, true);
      button.setAttribute('aria-expanded', 'true');
      targetMenu.classList.add('nav-menu__menu--active');
      parentMedia.setAttribute('aria-hidden', 'true');
      parentMedia.classList.remove('nav-menu__media-item--active');
      parentMedia.inert = true;
      targetMedia.setAttribute('aria-hidden', 'false');
      targetMedia.classList.add('nav-menu__media-item--active');
      targetMedia.inert = false;

      if (parentMedia.querySelector('video')) {
        parentMedia.querySelector('video').mediaControl.pause();
      }

      if (targetMedia.querySelector('video')) {
        targetMedia.querySelector('video').mediaControl.playIfAble();
      }

      const backButton = targetMenu.querySelector('.nav-menu__back');
      backButton.focus();

      document.removeEventListener('keydown', closeOnEscape);
      document.addEventListener('keydown', navigateUpOnEscape);
    });
  });

  backButtons.forEach((button) => {
    button.addEventListener('click', navigateUp);
  });

  document.addEventListener('keydown', closeOnEscape);
}

/**
 * Sets --nav-account-items-width, used to animate the account submenu's width
 * for browsers which don't support `interpolate-size: allow-keywords`.
 */
function setAccountFallbackWidth() {
  if (CSS.supports('interpolate-size: allow-keywords')) {
    return;
  }

  const expandable = navPrimary.querySelector('.nav__account-expandable');
  const gap = parseInt(window.getComputedStyle(expandable).getPropertyValue('gap'), 10);

  const items = expandable.querySelectorAll('.nav__account-label');
  const width = [...items].reduce((accumulator, item) => accumulator + item.offsetWidth + gap, 0);

  document.documentElement.style.setProperty('--nav-account-items-width', `${width}px`);
}

/**
 * Initialize the header component.
 */
export default function () {
  function init() {
    loadHeroLanding();

    header = document.querySelector('.header');
    navPrimary = header.querySelector('.nav-primary');
    navSecondary = header.querySelector('.nav-secondary');
    navMenu = document.querySelector('.nav-menu');
    skipLink = document.querySelector('.header__skip');

    largeMQ.addEventListener('change', setAccountFallbackWidth);

    calculateHeaderHeight();
    setBreakpointListeners();
    setScrollListener();
    setShowHideListener();
    setAccountFallbackWidth();
    initAccountWidget();
    initNavMenu();

    if (header.classList.contains('header--animated')) {
      const logoWrapper = header.querySelector('.nav__logo-wrapper');
      setTimeout(() => {
        logoWrapper.classList.remove('nav__logo-wrapper--pre');
      }, 2400);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
